import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  CUSTOM_FIELDS_BOOLEAN_SELECT_OPTIONS,
  CUSTOM_FIELDS_TYPES,
} from 'features/custom-fields/custom-fields.constants';
import { UntypedFormControl } from '@angular/forms';
import { MASK_TYPE, NumberMaskConfig, SmartForm } from '@certemy/ui-components';
import { take, takeUntil } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import { CUSTOM_FIELD_TYPES } from 'constants/constants';
import { PublicGlobalService } from 'services/public-global/public-global.service';
import { Country, State } from 'interfaces/public-global.interface';
import { ActivatedRoute } from '@angular/router';
import _sortBy from 'lodash-es/sortBy';

interface IOptions {
  id: string;
  name: string;
}

@Component({
  selector: 'cm-custom-field-value',
  templateUrl: './custom-field-value.component.html',
  styleUrls: ['./custom-field-value.component.scss'],
})
export class CustomFieldValueComponent implements OnInit, OnDestroy, OnChanges {
  @Input() value;
  @Input() fieldType;
  @Input() set options(options: Country[] | State[]) {
    const isUserCustomFields = this.activatedRoute.snapshot.data.policyObj === 'settings/users/custom_fields';
    const isCommunications =
      this.activatedRoute.snapshot.data.policyObj === 'settings/organization/organization_communications';
    if ((isUserCustomFields || isCommunications) && options) {
      switch (this.fieldType) {
        case CUSTOM_FIELD_TYPES.SELECT_COUNTRY:
          this.countries = options as Country[];
          this.handleCountrySelect(of(options) as Observable<Country[]>);
          break;
        case CUSTOM_FIELD_TYPES.SELECT_STATE:
          this.states = options as State[];
          this.handleStateSelect(of(_sortBy(options, ['name'])) as Observable<State[]>);
          break;
      }
    }
  }
  @Input() defaultReadonly = true;
  @Input() set disabled(value: boolean) {
    if (value) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }
  @Output() valueChange = new EventEmitter();

  numberMaskConfig: NumberMaskConfig = {
    digitLimit: 255,
    maskType: MASK_TYPE.LEADING_ZERO,
  };

  get fieldClass() {
    return { 'is-editable': this.defaultReadonly };
  }
  control = new UntypedFormControl('', { updateOn: 'blur' });
  defaultValue = new UntypedFormControl('');
  CUSTOM_FIELDS_TYPES = CUSTOM_FIELDS_TYPES;
  placeholder = 'No data';
  booleanSelectOptions: SmartForm.FieldOption[] = Object.values(CUSTOM_FIELDS_BOOLEAN_SELECT_OPTIONS);
  countriesOptions: IOptions[] = [];
  statesOptions: IOptions[] = [];
  states: State[] = [];
  countries: Country[] = [];
  private onDestroy$ = new Subject<void>();
  readonly CUSTOM_FIELD_TYPES = CUSTOM_FIELD_TYPES;

  phoneMaskConfig: NumberMaskConfig = {
    maskType: MASK_TYPE.PHONE_NUMBER,
    digitLimit: 10,
    prefix: '',
  };

  constructor(private publicGlobalService: PublicGlobalService, private activatedRoute: ActivatedRoute) {}

  ngOnInit() {
    let previousValue = this.control.value;
    this.control.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((value) => {
      let newValue = value;
      const emptyFieldIsNotChanged =
        (previousValue === null || previousValue === '') && (value === null || value === '');
      if (!emptyFieldIsNotChanged && value !== previousValue) {
        if ([CUSTOM_FIELD_TYPES.SELECT_COUNTRY, CUSTOM_FIELD_TYPES.SELECT_STATE].includes(this.fieldType)) {
          newValue = this.getNewValueFromOptions(value);
        }
        this.valueChange.emit(newValue);
      }
      previousValue = newValue;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const isUserCustomFields = this.activatedRoute.snapshot.data.policyObj === 'settings/users/custom_fields';
    const isCommunications =
      this.activatedRoute.snapshot.data.policyObj === 'settings/organization/organization_communications';
    if (changes.fieldType && changes.fieldType.previousValue !== changes.fieldType.currentValue) {
      if (!isUserCustomFields) {
        switch (this.fieldType) {
          case CUSTOM_FIELD_TYPES.SELECT_COUNTRY:
            this.handleCountrySelect(this.publicGlobalService.getCountries());
            break;
          case CUSTOM_FIELD_TYPES.SELECT_STATE:
            this.handleStateSelect(this.publicGlobalService.getStates(false));
            break;
          default:
            this.control.patchValue(this.value, { emitEvent: false });
            break;
        }
      }
      if (isCommunications) {
        const options = this.fieldType === CUSTOM_FIELD_TYPES.SELECT_COUNTRY ? this.countries : this.states;
        this.mappingValue(options);
      } else {
        this.control.patchValue(this.value, { emitEvent: false });
      }
    }

    if (this.fieldType !== CUSTOM_FIELD_TYPES.SELECT_COUNTRY && this.fieldType !== CUSTOM_FIELD_TYPES.SELECT_STATE) {
      this.control.patchValue(this.value);
    }
  }

  private handleCountrySelect(countryOptions$: Observable<Country[]>) {
    if (!this.countriesOptions.length) {
      this.loadData('countryId', countryOptions$, (options) => {
        this.countriesOptions = options;
        this.mappingValue(this.countries);
      });
    } else {
      this.mappingValue(this.countries);
    }
  }

  private handleStateSelect(stateOptions$: Observable<State[]>) {
    if (!this.statesOptions.length) {
      this.loadData('stateId', stateOptions$, (options) => {
        this.statesOptions = _sortBy(options, ['name']);
        this.mappingValue(this.states);
      });
    } else {
      this.mappingValue(this.states);
    }
  }

  private loadData(
    idType: 'countryId' | 'stateId',
    dataFeed$: Observable<Country[] | State[]>,
    dataAssigner: (data: IOptions[]) => void,
  ) {
    dataFeed$.pipe(take(1)).subscribe((data) => {
      const options = this.mappingOptions(data, idType);
      if (idType === 'countryId') {
        this.countries = data as Country[];
      } else {
        this.states = data as State[];
      }
      dataAssigner(options);
    });
  }

  private mappingOptions(options: Country[] | State[], key: string) {
    return (options as any).map((option) => ({ id: option[key].toString(), name: option.name }));
  }

  private mappingValue(options: Country[] | State[]) {
    const isCountryField = this.fieldType === CUSTOM_FIELD_TYPES.SELECT_COUNTRY;
    const findCodeName = (option: State | Country) =>
      isCountryField ? (option as Country).code === this.value : (option as State).stateCode === this.value;
    if (options && options.length) {
      const setNewValue = (option: State | Country) => (isCountryField ? option.countryId : (option as State).stateId);
      const selectedOption = (options as any).find((item) => item.name === this.value || findCodeName(item));
      const newValue = selectedOption ? setNewValue(selectedOption) : this.value;
      this.value
        ? this.control.patchValue(newValue.toString(), { emitEvent: false })
        : this.control.patchValue(newValue, { emitEvent: false });
    }
  }

  private getNewValueFromOptions(value: any): any {
    let options: IOptions[] = [];

    switch (this.fieldType) {
      case CUSTOM_FIELD_TYPES.SELECT_COUNTRY:
        options = this.countriesOptions;
        break;
      case CUSTOM_FIELD_TYPES.SELECT_STATE:
        options = this.statesOptions;
        break;
    }

    const index = options.findIndex((option) => option.id === value);
    return options[index].name;
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
